import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Input, Modal, message, Popconfirm, Select, Spin, Tag } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { canManageUsers } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { UsersNavigation } from "../components/UsersNavigation";
import {
	type AdminRole,
	getActionLabel,
	getPermissions,
	getResourceLabel,
	getRoles,
	type PermissionItem,
	submitRoleCreate,
	submitRoleDelete,
	submitRoleUpdate,
} from "../services/roles.backend";

const ROLE_NAME_MAP: Record<string, string> = {
	SUPER_ADMIN: "Super Administrator",
	ORG_ADMIN: "Tashkilot Administratori",
	SALES_MANAGER: "Sotuv Menejeri",
};

const ROLE_DESCRIPTION_MAP: Record<string, string> = {
	"Super Administrator - full system access": "Tizimning to'liq huquqli boshqaruvchisi",
	"Organization Admin - manages users and organization":
		"Tashkilot administratori - foydalanuvchilar va tashkilotni boshqaradi",
	"Sales Manager - manages customers and sales":
		"Sotuv menejeri - mijozlar va sotuvlarni boshqaradi",
};

function parseInherits(inherits: any): string[] {
	if (!inherits) {
		return [];
	}
	if (Array.isArray(inherits)) {
		return inherits;
	}
	if (typeof inherits === "string") {
		try {
			const parsed = JSON.parse(inherits);
			return Array.isArray(parsed) ? parsed : [inherits];
		} catch {
			return [inherits];
		}
	}
	return [];
}

type RoleFormModalProps = {
	open: boolean;
	role: AdminRole | null;
	allRoles: AdminRole[];
	allPermissions: { name: string; actions: string[] }[];
	isSubmitting: boolean;
	onClose: () => void;
	onSubmit: (payload: any) => void;
};

function RoleFormModal({
	open,
	role,
	allRoles,
	allPermissions,
	isSubmitting,
	onClose,
	onSubmit,
}: RoleFormModalProps) {
	const [id, setId] = useState("");
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [selectedPermissions, setSelectedPermissions] = useState<PermissionItem[]>([]);
	const [inherits, setInherits] = useState<string[]>([]);
	const [color, setColor] = useState("#2563eb");

	const isEditMode = Boolean(role);

	useEffect(() => {
		if (open) {
			if (role) {
				setId(role.id);
				setName(role.name);
				setDescription(role.description || "");
				setSelectedPermissions(role.permissions || []);
				setInherits(parseInherits(role.inherits));
				setColor((role.metadata?.color as string) || "#2563eb");
			} else {
				setId("");
				setName("");
				setDescription("");
				setSelectedPermissions([]);
				setInherits([]);
				setColor("#2563eb");
			}
		}
	}, [role, open]);

	const handlePermissionChange = (resource: string, action: string, checked: boolean) => {
		if (checked) {
			setSelectedPermissions((prev) => [...prev, { resource, action }]);
		} else {
			setSelectedPermissions((prev) =>
				prev.filter((p) => !(p.resource === resource && p.action === action))
			);
		}
	};

	const handleResourceAllChange = (resource: string, actions: string[], checked: boolean) => {
		if (checked) {
			setSelectedPermissions((prev) => {
				const filtered = prev.filter((p) => p.resource !== resource);
				const toAdd = actions.map((action) => ({ resource, action }));
				return [...filtered, ...toAdd];
			});
		} else {
			setSelectedPermissions((prev) => prev.filter((p) => p.resource !== resource));
		}
	};

	const isActionChecked = (resource: string, action: string) => {
		return selectedPermissions.some((p) => p.resource === resource && p.action === action);
	};

	const isResourceAllChecked = (resource: string, actions: string[]) => {
		return actions.every((action) => isActionChecked(resource, action));
	};

	const isResourceIndeterminate = (resource: string, actions: string[]) => {
		const checkedCount = actions.filter((action) => isActionChecked(resource, action)).length;
		return checkedCount > 0 && checkedCount < actions.length;
	};

	const handleSubmit = () => {
		if (!(name.trim() && (isEditMode || id.trim()))) {
			message.warning("Iltimos, barcha majburiy maydonlarni to'ldiring.");
			return;
		}

		onSubmit({
			id: isEditMode ? undefined : id.trim(),
			name: name.trim(),
			description: description.trim(),
			permissions: selectedPermissions,
			inherits,
			metadata: { color },
		});
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			title={isEditMode ? "Rolni tahrirlash" : "Yangi rol yaratish"}
			width={800}
			className="crm-role-modal"
		>
			<div
				className="crm-role-modal-body"
				style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "8px" }}
			>
				<div className="crm-form-row">
					<label style={{ flex: 1 }} className="crm-field" htmlFor="role-id">
						<span>Rol IDsi (Tizim kodi) *</span>
						<Input
							id="role-id"
							placeholder="admin_role"
							value={id}
							onChange={(e) => setId(e.target.value)}
							disabled={isEditMode}
						/>
					</label>

					<label style={{ flex: 1 }} className="crm-field" htmlFor="role-name">
						<span>Rol nomi *</span>
						<Input
							id="role-name"
							placeholder="Administrator"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</label>
				</div>

				<div className="crm-form-row">
					<div style={{ flex: 1 }} className="crm-field">
						<span>Meros oladigan rollar</span>
						<Select
							mode="multiple"
							placeholder="Ushbu rol meros oladigan boshqa rollarni tanlang"
							style={{ width: "100%" }}
							value={inherits}
							onChange={setInherits}
							getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
							notFoundContent={
								allRoles.length === 0 ? "Ro'llar yuklanmoqda..." : "Ro'llar topilmadi"
							}
							options={allRoles
								.filter((r) => r.id !== role?.id)
								.map((r) => ({ label: r.name, value: r.id }))}
						/>
					</div>

					<label style={{ width: "240px" }} className="crm-field" htmlFor="role-color">
						<span>Rol rangi (Vizual)</span>
						<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
							<Input
								id="role-color"
								type="color"
								value={color}
								onChange={(e) => setColor(e.target.value)}
								style={{ width: "45px", height: "36px", padding: "2px", cursor: "pointer" }}
							/>
							<Input
								placeholder="#2563eb"
								value={color}
								onChange={(e) => setColor(e.target.value)}
								style={{ flex: 1 }}
							/>
						</div>
					</label>
				</div>

				<div className="crm-form-row">
					<label style={{ flex: 1 }} className="crm-field" htmlFor="role-description">
						<span>Tavsifi</span>
						<Input
							id="role-description"
							placeholder="Tizim bo'yicha to'liq huquqli rol"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</label>
				</div>

				<div style={{ marginBottom: "16px" }}>
					<span
						style={{
							fontWeight: 600,
							display: "block",
							marginBottom: "10px",
							color: "var(--crm-ink)",
						}}
					>
						Huquqlar & Ruxsatlar
					</span>
					<div className="crm-permissions-grid">
						{allPermissions.map((res) => (
							<div key={res.name} className="crm-permission-card">
								<div className="crm-permission-card-header">
									<strong>{getResourceLabel(res.name)}</strong>
									<Checkbox
										checked={isResourceAllChecked(res.name, res.actions)}
										indeterminate={isResourceIndeterminate(res.name, res.actions)}
										onChange={(e) =>
											handleResourceAllChange(res.name, res.actions, e.target.checked)
										}
									>
										Barchasi
									</Checkbox>
								</div>
								<div className="crm-permission-card-body">
									{res.actions.map((action) => (
										<Checkbox
											key={action}
											checked={isActionChecked(res.name, action)}
											onChange={(e) => handlePermissionChange(res.name, action, e.target.checked)}
										>
											{getActionLabel(action)}
										</Checkbox>
									))}
								</div>
							</div>
						))}
					</div>
				</div>

				<div
					style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}
				>
					<Button disabled={isSubmitting} onClick={onClose}>
						Bekor qilish
					</Button>
					<Button
						type="primary"
						className="crm-primary-btn"
						loading={isSubmitting}
						onClick={handleSubmit}
					>
						Saqlash
					</Button>
				</div>
			</div>
		</Modal>
	);
}

export function RolesPage() {
	const { role: userRole } = useOutletContext<{ role: AppRole }>();
	const canManage = canManageUsers(userRole);
	const [searchQuery, setSearchQuery] = useState("");
	const [modalOpen, setModalOpen] = useState(false);
	const [editingRole, setEditingRole] = useState<AdminRole | null>(null);
	const queryClient = useQueryClient();

	const { data: rolesData, isLoading: isRolesLoading } = useQuery({
		queryKey: ["roles-list"],
		queryFn: () => getRoles(),
	});

	const { data: permissionsData, isLoading: isPermissionsLoading } = useQuery({
		queryKey: ["permissions-list"],
		queryFn: getPermissions,
	});

	const createRoleMutation = useMutation({
		mutationFn: submitRoleCreate,
		onSuccess: async () => {
			message.success("Rol muvaffaqiyatli qo'shildi!");
			setModalOpen(false);
			await queryClient.invalidateQueries({ queryKey: ["roles-list"] });
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const updateRoleMutation = useMutation({
		mutationFn: ({ id, payload }: { id: string; payload: any }) => submitRoleUpdate(id, payload),
		onSuccess: async () => {
			message.success("Rol muvaffaqiyatli yangilandi!");
			setModalOpen(false);
			setEditingRole(null);
			await queryClient.invalidateQueries({ queryKey: ["roles-list"] });
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const deleteRoleMutation = useMutation({
		mutationFn: submitRoleDelete,
		onSuccess: async () => {
			message.success("Rol muvaffaqiyatli o'chirildi!");
			await queryClient.invalidateQueries({ queryKey: ["roles-list"] });
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const roles = rolesData?.items || [];
	const permissions = permissionsData || [];

	const filteredRoles = useMemo(() => {
		if (!searchQuery) {
			return roles;
		}
		const query = searchQuery.toLowerCase();
		return roles.filter(
			(r: AdminRole) =>
				r.name.toLowerCase().includes(query) ||
				ROLE_NAME_MAP[r.name]?.toLowerCase().includes(query) ||
				r.description?.toLowerCase().includes(query) ||
				(r.description && ROLE_DESCRIPTION_MAP[r.description]?.toLowerCase().includes(query))
		);
	}, [roles, searchQuery]);

	const handleEditClick = (roleItem: AdminRole) => {
		if (!canManage) {
			message.warning("Huquqingiz yetarli emas.");
			return;
		}
		setEditingRole(roleItem);
		setModalOpen(true);
	};

	const handleCreateClick = () => {
		if (!canManage) {
			message.warning("Huquqingiz yetarli emas.");
			return;
		}
		setEditingRole(null);
		setModalOpen(true);
	};

	const handleFormSubmit = (payload: any) => {
		if (editingRole) {
			updateRoleMutation.mutate({ id: editingRole.id, payload });
		} else {
			createRoleMutation.mutate(payload);
		}
	};

	// Render grid of cards instead of table
	return (
		<div className="crm-roles-page">
			<div className="crm-page-head">
				<div>
					<h1>Rollar va Huquqlar</h1>
					<p>Tizim rollarini yaratish va ularga tegishli resurslar bo'yicha ruxsatlarni sozlash</p>
				</div>
				<Button className="crm-primary-btn" icon={<PlusOutlined />} onClick={handleCreateClick}>
					Rol qo'shish
				</Button>
			</div>

			<UsersNavigation activeTab="roles" />

			<div style={{ marginBottom: "16px", maxWidth: "320px" }}>
				<Input
					placeholder="Rol nomini qidirish..."
					allowClear
					prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
				/>
			</div>

			{isRolesLoading || isPermissionsLoading ? (
				<div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
					<Spin size="large" />
				</div>
			) : filteredRoles.length === 0 ? (
				<div
					className="crm-card"
					style={{ padding: "40px", textAlign: "center", color: "var(--crm-ink-3)" }}
				>
					Rollar topilmadi.
				</div>
			) : (
				<div className="crm-roles-grid">
					{filteredRoles.map((record: AdminRole) => {
						const rColor = (record.metadata?.color as string) || "#2563eb";
						const inheritsList = parseInherits(record.inherits);
						return (
							<div key={record.id} className="crm-role-card">
								<div className="crm-role-card-header">
									<div className="crm-role-card-title-group">
										<span className="crm-role-color-dot" style={{ background: rColor }} />
										<h3>{ROLE_NAME_MAP[record.name] || record.name}</h3>
									</div>
									<div className="crm-role-card-actions">
										<Button
											icon={<EditOutlined />}
											size="small"
											type="text"
											onClick={() => handleEditClick(record)}
										/>
										<Popconfirm
											title="Rolni o'chirish"
											description="Rostdan ham ushbu rolni o'chirmoqchimisiz?"
											onConfirm={() => deleteRoleMutation.mutate(record.id)}
											okText="Ha"
											cancelText="Yo'q"
											okButtonProps={{ danger: true }}
										>
											<Button icon={<DeleteOutlined />} size="small" type="text" danger />
										</Popconfirm>
									</div>
								</div>
								<div className="crm-role-card-body">
									{record.description && (
										<p className="crm-role-desc">
											{ROLE_DESCRIPTION_MAP[record.description] || record.description}
										</p>
									)}
									<div className="crm-role-meta-row">
										<span className="crm-role-meta-label">Tizim kodi:</span>
										<Tag color="blue">{record.id}</Tag>
									</div>
									{inheritsList.length > 0 && (
										<div className="crm-role-meta-row">
											<span className="crm-role-meta-label">Merosxo'rlik:</span>
											<div className="crm-role-tags-wrap">
												{inheritsList.map((item) => (
													<Tag key={item}>{item}</Tag>
												))}
											</div>
										</div>
									)}
									<div className="crm-role-permissions-section">
										<h4>Ruxsat etilgan resurslar:</h4>
										{!record.permissions || record.permissions.length === 0 ? (
											<span className="crm-role-no-perms">Huquqlar yo'q</span>
										) : (
											<div className="crm-role-permissions-list">
												{(() => {
													const grouped: Record<string, string[]> = {};
													for (const p of record.permissions) {
														if (!grouped[p.resource]) {
															grouped[p.resource] = [];
														}
														grouped[p.resource].push(p.action);
													}
													const groupedEntries = Object.entries(grouped);
													const visibleEntries = groupedEntries.slice(0, 6);
													const hiddenCount = groupedEntries.length - visibleEntries.length;

													return (
														<>
															{visibleEntries.map(([resource, actions]) => {
																if (resource === "*" && actions.includes("*")) {
																	return (
																		<Tag key={resource} color="gold" style={{ fontWeight: 600 }}>
																			To'liq ruxsat (Barcha resurslar)
																		</Tag>
																	);
																}
																const resName = getResourceLabel(resource);
																const actionNames = actions
																	.map((action) => getActionLabel(action))
																	.join(", ");
																return (
																	<Tag key={resource} color="geekblue">
																		{resName}: {actionNames}
																	</Tag>
																);
															})}
															{hiddenCount > 0 ? (
																<Tag color="default">+{hiddenCount} resurs</Tag>
															) : null}
														</>
													);
												})()}
											</div>
										)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<RoleFormModal
				open={modalOpen}
				role={editingRole}
				allRoles={roles}
				allPermissions={permissions}
				isSubmitting={createRoleMutation.isPending || updateRoleMutation.isPending}
				onClose={() => {
					setModalOpen(false);
					setEditingRole(null);
				}}
				onSubmit={handleFormSubmit}
			/>
		</div>
	);
}

export default RolesPage;
